import { useLayoutEffect, useState,useRef, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { DelayEntry } from "../DelayEntry/DelayEntry";
import { DCDialog } from "../Wrappers/DCDialog";
import "./DCTable.scss";
import { AuthContext } from "../../providers/AuthProvider";
import { DelayListRecordWithAction } from "../../Model/DelayListRecordWithAction";


//Component properties interface
interface DCTableProps {
  columnsDefinition: DCTableColumns[];
  tableData: any[];
  keyFieldName: string;
  searchDate: string;
  onDeleteClick: (id: string,aircraft:string,station:string,deptFlight:string) => void;
  onSortChange: (event: SortRequestModel) => void;
  onDelayEntryDialogClose: () => void;
  onDeleteEntry:(id: string,aircraft:string,station:string,deptFlight:string)=>void;
  onError: (data: string) => void;
  closeDialog: () => void;
  selectedRowId: string | null;
  onRowSelect: (rowId: string) => void; 
  onSaveFlightDelayDetail: (savedRecord:DelayListRecordWithAction)=>void;
}

export interface DCTableColumns {
  key: string;
  name: string;
  sortable: boolean;
  dataFormat: "date" | "string";
  clickable: boolean;
  width?: number;
  limitline?: boolean;
}

export interface SortRequestModel {
  fieldName: string;
  direction: "asc" | "desc";
}

export function SortItems(a: any, b: any, event: SortRequestModel): number {
  if (event.fieldName) {
    const columnA = a[event.fieldName];
    const columnB = b[event.fieldName];
    if (columnA < columnB) {
      return event.direction === "asc" ? -1 : 1;
    }
    if (columnA > columnB) {
      return event.direction === "asc" ? 1 : -1;
    }
  }
  return 0;
}

export const DCTable = ({
  keyFieldName,
  columnsDefinition,
  tableData,
  onSortChange,
  searchDate,
  onDeleteClick,
  onDelayEntryDialogClose,
  onDeleteEntry,
  selectedRowId,
  onRowSelect,
  onSaveFlightDelayDetail
}: DCTableProps) => {
  //States
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [openDialog, setDialogOpen] = useState(false);
  const [aircraftvalue, setAircraftValue] = useState("");
  const [depflightvalue, setDepFlightValue] = useState("");
  const [stationvalue, setStationValue] = useState("");
  const [source, setSource] = useState("");
  const [MaintDncId,setMaintDncId]=useState("");
  const [scheddepartvalue,setSchedDepartValue]=useState("");
  const [delaytimelenvalue,setDelayTimeLenValue]=useState("");
  const [actualdepartvalue,setActualDepartValue]=useState("");
  const [selectedRow, setSelectedRow] = useState<string | null>(selectedRowId);
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const [flightSegmentId, setFlightSegmentId] = useState<number>(0);
  const [rowFlightDate, setRowFlightDate] = useState("");
  const scrollDivRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);

  const errorRuleEventHandler = (data: string) => {
    setDialogOpen(false);
    onDelayEntryDialogClose();
  };

  /** User clicks cancel button in the dialog window */
  const cancelRuleEventHandler = () => {
    console.info("DC entry cancelled");
    setDialogOpen(false);
    onDelayEntryDialogClose();
  };

  //Event handlers
  const handleSort = (column: string) => {
    let srtDirection: "asc" | "desc" = "asc";
    if (sortColumn === column) {
      srtDirection = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortDirection(srtDirection);
    setSortColumn(column);
    onSortChange({ fieldName: column, direction: srtDirection });
    
  };
  const handleRowClick = (
    aircraft: string,
    stn: string,
    deptFlight: string,
    source: string,
    MaintDncId:string,
    delayTimeLen: string,
    scheddepart: string,
    actualdepart: string,
    rowIndex: string,
    flightSegmentId: number,
    flightDate: string,
  ) => {
    setAircraftValue(aircraft);
    setStationValue(stn);
    setDepFlightValue(deptFlight);
    setSource(source);
    setMaintDncId(MaintDncId);
    setDelayTimeLenValue(delayTimeLen);
    setSchedDepartValue(scheddepart);
    setActualDepartValue(actualdepart);
    setFlightSegmentId(flightSegmentId);
    setRowFlightDate(flightDate);
    setSelectedRow(rowIndex);
    onRowSelect(rowIndex);
  };
  const truncateText = (text: string, limit: number) => {
    if (text.length > limit) {
        return text.substring(0, limit) + '...';
    }
    return text;
};

 // Calculate the max height so the table fits in the visible window without scrolling the window itself
  useLayoutEffect(() => {
    function updateHeight() {
      if (scrollDivRef.current) {
        // Get the distance from the top of the viewport to the table
        const rect = scrollDivRef.current.getBoundingClientRect();
        // Calculate available space below the table to the bottom of the window
        const available = window.innerHeight - rect.top - 20; // 10px for some bottom margin/padding
        setContainerHeight(available > 200 ? available : 200); // Minimum height fallback
      }
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

 // Scroll to center the selected row after render
  useEffect(() => {
  if (selectedRow && rowRefs.current[selectedRow] && scrollDivRef.current) {
    const row = rowRefs.current[selectedRow];
    const container = scrollDivRef.current;
    if (row && container) {
      const rowTop = row.offsetTop;
      const rowBottom = rowTop + row.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;
      const containerBottom = containerScrollTop + containerHeight-60;

      // Only scroll if the row is not fully visible
      if (rowTop < containerScrollTop || rowBottom > containerBottom) {
        // Center the row in the scrollable container
        container.scrollTop = rowTop - (containerHeight / 2) + (row.offsetHeight / 2);
      }
    }
  }
}, [selectedRow, tableData.length]);

  useEffect(() => {
    setSelectedRow(selectedRowId);
  }, [selectedRowId]);

  return (
    <div ref={scrollDivRef} style={{
        height: containerHeight ? `${containerHeight}px` : "auto",
        overflowY: "auto"
      }}>
      <table data-testid='table' className="auro_table">
        <thead data-testid='header'>
          <tr key="header">
            {columnsDefinition.map((column) => (
              <td
                key={column.key}
                className={
                  column.key === "discrepancyRemark" || column.key === "comments"
                    ? "TableHeader left-align-cell"
                    : "TableHeader center-align-cell"
                }
                style={{ width: column.width + "%" }}
                onClick={() =>
                  column.sortable ? handleSort(column.key) : undefined
                }
              >
                <span className="TableHeaderContent">
                  {column.name}
                  {sortColumn === column.key ? (
                    sortDirection === "asc" ? (
                      <ReactSVG
                        className="TableHeaderContentSVG"
                        src="https://cdn.jsdelivr.net/npm/@alaskaairux/icons@latest/dist/icons/interface/arrow-up.svg"
                      />
                    ) : (
                      <ReactSVG
                        className="TableHeaderContentSVG"
                        src="https://cdn.jsdelivr.net/npm/@alaskaairux/icons@latest/dist/icons/interface/arrow-down.svg"
                      />
                    )
                  ) : undefined}
                </span>
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
  {tableData.length === 0 ? (
    <tr>
      <td colSpan={columnsDefinition.length + 1}>
        No data found or an error occurred while retrieving data.{" "}
      </td>
    </tr>
  ) : (
    tableData.map((row, index) => {     
     const uniqueId = `${row.flightSegmentId.toString().trim()}${row.dptFlightNo.padStart(4, "0")}`;
      return (
        <tr key={uniqueId} ref={el => { rowRefs.current[uniqueId] = el; }} className={uniqueId === selectedRow ? 'highlighted-row' : ''}>
          {columnsDefinition.map((column, columnIndex) => (
            <td
              key={column.key.concat(index.toString())}
              onClick={() => {
                if (columnIndex === 0) {
                  setDialogOpen(true);
                  handleRowClick(
                    row.ac,
                    row.stn,
                    row.dptFlightNo,
                    row.source,
                    row.maintDncId,
                    row.delayTimeLen,
                    row.scheddepart,
                    row.actualdepart,
                    uniqueId,
                    row.flightSegmentId,
                    row.flightDate ?? ""
                  );
                }
              }}
              className={
                columnIndex === 0
                  ? "clickable-cell"
                  : column.key === "discrepancyRemark" || column.key === "comments"
                  ? "left-align-cell"
                  : "center-align-cell"
              }
              style={{ width: column.width + "%"  }}
            >
              {column.limitline ? (
                <div className="line-clamp-4">{truncateText(row[column.key], 320)}</div>
              ) : (
                row[column.key]
              )}
            </td>
          ))}
        </tr>
      );
    })
  )}
</tbody>
      </table>
      <DCDialog
        open={openDialog}
        onclose={() => cancelRuleEventHandler()}
        header={"Delay Details "}
        size="lg"
      >
        <DelayEntry
          onCancel={() => cancelRuleEventHandler()}
          onError={(data: string) => errorRuleEventHandler(data)}
          onDelete={(MaintDncId:string,aircraftvalue:string,stationvalue:string,depflightvalue:string)=>onDeleteEntry(MaintDncId,aircraftvalue,stationvalue,depflightvalue)}
          aircraft={aircraftvalue}
          flight={depflightvalue}
          station={stationvalue}
          source={source}
          searchDate={rowFlightDate || searchDate}
          airline={""}
          MaintDncId={MaintDncId}
          scheddepart={scheddepartvalue}
          delayTimeLen={delaytimelenvalue}
          actualdepart={actualdepartvalue}
          closeDialog={() => cancelRuleEventHandler()}
          onSaveFlightDelayDetail={onSaveFlightDelayDetail}
           flightSegmentId={flightSegmentId}
        />
      </DCDialog>
    </div>
  );
};
